import classNames from 'classnames'
import isYouTubeVideo, { getYouTubeVideoID } from 'src/utils/youtube'

import type { TechnicalContentMainMedia, TechnicalContentTheme } from './types'

type MainMediaProps = {
  media: TechnicalContentMainMedia
  theme: TechnicalContentTheme
}

const isValidPixlVideo = (url?: string) => {
  if (!url?.trim()) {
    return false
  }

  try {
    const parsedUrl = new URL(url)

    return ['http:', 'https:'].includes(parsedUrl.protocol)
  } catch {
    return false
  }
}

const getVideoSrc = (media: TechnicalContentMainMedia) => {
  const videoUrl = media.videoUrl?.trim() ?? ''

  if (media.videoProvider === 'youtube') {
    if (!isYouTubeVideo(videoUrl)) {
      return null
    }

    const videoId = getYouTubeVideoID(videoUrl)

    if (!videoId) {
      return null
    }

    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`
  }

  if (media.videoProvider === 'pixl' && isValidPixlVideo(videoUrl)) {
    return videoUrl
  }

  return null
}

export default function MainMedia({ media, theme }: MainMediaProps) {
  const isDarkTheme = theme === 'dark'

  if (media.mediaType === 'image') {
    if (!media.image?.trim() || !media.imageAlt?.trim()) {
      return null
    }

    return (
      <div
        className="aspect-[4/5] overflow-hidden bg-black md:aspect-[345/319]"
        data-testid="technical-content-main-image"
      >
        <img
          src={media.image}
          alt={media.imageAlt}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </div>
    )
  }

  const videoSrc = getVideoSrc(media)

  if (!videoSrc) {
    return (
      <div
        className={classNames(
          'flex h-[495px] items-center justify-center border px-4 py-8 text-center text-body-sm md:h-[640px]',
          isDarkTheme
            ? 'border-technicalContent20BorderInverted bg-technicalContent20SurfaceInverted text-technicalContent20BodyInverted'
            : 'border-technicalContent20Border bg-technicalContent20Surface text-technicalContent20Body'
        )}
        data-testid="technical-content-invalid-video"
      >
        Video indisponivel para este item.
      </div>
    )
  }

  return (
    <div
      className="w-full overflow-hidden bg-black md:w-[640px]"
      data-testid="technical-content-main-video"
    >
      <iframe
        src={videoSrc}
        title={media.videoTitle ?? 'Video principal'}
        className="h-auto w-full aspect-video md:h-[637px] md:w-[640px] md:aspect-auto"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}
